import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { LoadStatus, InstallationStatus } from "@/config/constants";
import { queryKeys } from "@/lib/query-keys";

export function useLoadsListMutations() {
  const queryClient = useQueryClient();

  // Update load status
  const updateLoadStatusMutation = useMutation({
    mutationFn: async ({ loadId, status }: { loadId: string; status: LoadStatus }) => {
      const { error } = await supabase
        .from("installation_loads")
        .update({ status })
        .eq("id", loadId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.loads.all });
      toast.success("Kuorman tila päivitetty");
    },
    onError: (error) => {
      console.error("Error updating load status:", error);
      toast.error("Virhe tilan päivityksessä");
    },
  });

  // Update installation status
  const updateInstallationStatusMutation = useMutation({
    mutationFn: async ({ 
      installationId, 
      status, 
      notes 
    }: { 
      installationId: string; 
      status: InstallationStatus; 
      notes?: string;
    }) => {
      const updates: Record<string, unknown> = { status };
      if (status === "completed") {
        updates.completed_date = new Date().toISOString().split("T")[0];
      }
      if (notes) {
        updates.notes = notes;
      }
      
      const { error } = await supabase
        .from("installations")
        .update(updates)
        .eq("id", installationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.loads.installations() });
      toast.success("Tehtävän tila päivitetty");
    },
    onError: (error) => {
      console.error("Error updating installation status:", error);
      toast.error("Virhe tilan päivityksessä");
    },
  });

  const updateLoadStatus = (loadId: string, status: LoadStatus) => {
    updateLoadStatusMutation.mutate({ loadId, status });
  };

  const updateInstallationStatus = (
    installationId: string, 
    status: InstallationStatus, 
    notes?: string
  ) => {
    updateInstallationStatusMutation.mutate({ installationId, status, notes });
  };

  return {
    updateLoadStatus,
    updateInstallationStatus,
    isUpdatingLoadStatus: updateLoadStatusMutation.isPending,
    isUpdatingInstallationStatus: updateInstallationStatusMutation.isPending,
  };
}
